import { Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import ButtonSave from "./createProductHeader/formActions/ButtonSave";
import ButtonSubmit from "./createProductHeader/formActions/ButtonSubmit";
import ActionButton from "./inventoryDataGrid/cellActions.tsx/ActionButton";
import { useGetProductDetails } from "../../api/products";
import { UserActions } from "../../models/Authorisation";
import { AUCTION_TYPE, PRODUCT_STATUS } from "../../models/Product";
import { MODE, useFormStore } from "../../stores/formStore";
import { useUserStore } from "../../stores/userStore";

const enum FormAction {
  SAVE = "Save",
  SUBMIT = "Submit",
  EDIT = "Edit",
  DELETE = "Delete",
  APPROVE = "Approve",
  REJECT = "Reject",
  DEACTIVATE = "Deactivate",
  ACTIVATE = "Activate",
  RESUBMIT = "Resubmit",
}

type FormActions = FormAction[];

const FormHeader = () => {
  const { productId } = useParams<{
    productId: string;
  }>();

  const mode = useFormStore(state => state.mode);

  const isEditMode = mode === MODE.EDIT;

  const userActions = useUserStore(state => state.userActions);

  const { data } = useGetProductDetails({ productId });

  const [formActions, setFormActions] = useState<FormActions>(
    isEditMode ? [FormAction.SAVE, FormAction.SUBMIT] : [],
  );

  useEffect(() => {
    const computeButtons =
      (productId && data?.status && userActions) || (!productId && userActions);

    if (computeButtons) {
      const allowedActions = userActions[data?.status ?? PRODUCT_STATUS.DRAFT];

      const updatedFormActions = allowedActions?.reduce(
        (acc: FormActions, action) => {
          switch (action) {
            case UserActions.Approve:
              acc.push(FormAction.APPROVE);
              break;

            case UserActions.Reject:
              acc.push(FormAction.REJECT);
              break;

            case UserActions.Activate:
              acc.push(FormAction.ACTIVATE);
              break;

            case UserActions.Deactivate:
              acc.push(FormAction.DEACTIVATE);
              break;

            case UserActions.Review:
              isEditMode
                ? acc.push(FormAction.SAVE, FormAction.RESUBMIT)
                : acc.push(FormAction.EDIT);
              break;

            case UserActions.Edit:
              isEditMode
                ? acc.push(FormAction.SAVE, FormAction.SUBMIT)
                : acc.push(FormAction.EDIT);
              break;

            case UserActions.Delete:
              productId && acc.push(FormAction.DELETE);
              break;
          }

          return acc;
        },
        [],
      );

      updatedFormActions && setFormActions(updatedFormActions);
    }
  }, [data?.status, userActions, isEditMode]);

  return (
    <Stack direction="row" spacing={1} data-testid="form-header-component">
      {formActions.map(action => {
        const key = `${action}-${productId}`;

        switch (action) {
          case FormAction.APPROVE:
            return (
              <ActionButton
                key={key}
                action={UserActions.Approve}
                productId={productId as string}
              />
            );

          case FormAction.REJECT:
            return (
              <ActionButton
                key={key}
                action={UserActions.Reject}
                productId={productId as string}
              />
            );

          case FormAction.ACTIVATE:
            return (
              <ActionButton
                key={key}
                action={UserActions.Activate}
                productId={productId as string}
                disabled={data?.auctionType === AUCTION_TYPE.SPECIFIC}
              />
            );

          case FormAction.DEACTIVATE:
            return (
              <ActionButton
                key={key}
                action={UserActions.Deactivate}
                productId={productId as string}
              />
            );

          case FormAction.RESUBMIT:
            return (
              <ButtonSubmit
                productId={productId as string}
                key={key}
                label="Resubmit"
                productName={data?.productName}
              />
            );

          case FormAction.SAVE:
            return <ButtonSave productId={productId as string} key={key} />;

          case FormAction.SUBMIT:
            return (
              <ButtonSubmit
                productId={productId as string}
                key={key}
                productName={data?.productName}
              />
            );

          case FormAction.EDIT:
            return (
              <ActionButton
                key={key}
                action={UserActions.Edit}
                productId={productId as string}
              />
            );

          case FormAction.DELETE:
            return (
              <ActionButton
                key={key}
                action={UserActions.Delete}
                productId={productId as string}
                productName={data?.productName}
              />
            );
        }
      })}
    </Stack>
  );
};

export default FormHeader;
